import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { Loading } from '../../../components';

/**
 * PRODUCT UPDATES — the one channel that belongs to no store.
 *
 * Reached from the section toolbar, beside New store, because it is not a
 * store and does not belong in the store list.
 *
 * ⚠️ THE AUDIENCE IS NOT SET HERE. Who hears it is decided store by store, in
 * each store's own panel, defaulting to that store's owner and nobody else
 * (Alex, 2026-10-27). An earlier pass of this build made it three radio
 * buttons across the whole platform, which is the same mistake as the
 * hard-coded rule it replaced, only easier to reach. This panel shows the
 * channel and who is in it; the rows link to the store that decides.
 */

const ILock = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

export function ProductUpdatesPanel({ onOpenStore }) {
  const [state, setState] = useState({ loading: true, data: null });
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    setError('');
    try {
      const { data } = await axios.get('/api/roam-admin/updates-channel');
      setState({ loading: false, data });
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not read the Product Updates channel.');
      setState({ loading: false, data: null });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.loading) return <section className="rc-sc__panel"><Loading /></section>;

  const detail = state.data;

  if (!detail) {
    return (
      <section className="rc-sc__panel">
        <p className="rc-sc__error" role="alert">{error || 'Could not read the channel.'}</p>
        <div><button type="button" className="rc-sc__btn" onClick={load}>Try again</button></div>
      </section>
    );
  }

  const stores = detail.stores || [];

  return (
    <section className="rc-sc__panel" aria-label="Product Updates">
      <div className="rc-sc__panel-head">
        <h3>{detail.name || 'Retail Circle Product Updates'}</h3>
        <span className="rc-sc__panel-id">one channel, no store</span>
        <span className="rc-sc__spacer" />
        <span className={`rc-sc__state ${detail.is_provisioned ? 'rc-sc__state--ok' : ''}`}>
          {detail.is_provisioned ? 'Live' : 'Not provisioned'}
        </span>
      </div>

      {detail.provision_error && (
        <p className="rc-sc__note rc-sc__note--bad"><span>{detail.provision_error}</span></p>
      )}

      <p className="rc-sc__note">
        <ILock />
        <span>
          <strong>Nobody but a Retail Circle master admin can post here.</strong> Recipients see it in
          Messages and cannot reply — it is an announcement board, not a conversation. Posting is done
          from the Messages page.
        </span>
      </p>

      <div className="rc-sc__band">
        <div className="rc-sc__band-head">
          <h4>Who is in it</h4>
          <span className="rc-sc__band-count">
            {detail.reach ?? 0} {detail.reach === 1 ? 'person' : 'people'} across {stores.length} {stores.length === 1 ? 'store' : 'stores'}
          </span>
        </div>

        <p className="rc-sc__note" style={{ borderStyle: 'dashed', background: 'transparent' }}>
          <span>
            Decided <strong>store by store</strong>, in each store’s own panel — a store knows who
            should hear from us better than one rule across all of them. By default that is the
            store <strong>owner, and only the owner</strong>.
          </span>
        </p>

        {stores.length === 0 ? (
          <p className="rc-sc__hint">Nobody is in it yet.</p>
        ) : (
          <div className="rc-sc__people">
            {stores.map((row) => (
              <button
                type="button"
                key={row.id}
                className="rc-sc__person"
                style={{ textAlign: 'left', cursor: 'pointer' }}
                onClick={() => onOpenStore?.({ id: row.id, name: row.name })}
              >
                <span className="rc-sc__person-body">
                  <span className="rc-sc__person-name">{row.name}</span>
                  <span className="rc-sc__person-meta">{(row.names || []).join(', ') || '—'}</span>
                </span>
                <span className="rc-sc__person-fixed">{row.count} of {row.of}</span>
              </button>
            ))}
          </div>
        )}

        <p className="rc-sc__hint">
          A row opens that store’s panel, where who gets it is chosen. Stores with nobody in it are
          not listed. Changes take effect the next time the channel syncs; nobody is emailed.
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <a className="rc-sc__btn" href="/messages" style={{ textDecoration: 'none' }}>Open in Messages</a>
      </div>
    </section>
  );
}

export default ProductUpdatesPanel;
